import { CopyButton } from './CopyButton';
import type { GuardrailModelsOut, MyModelsOut } from '../types';

interface ModelCatalogProps {
  /** null = still loading; 'error' = the request failed. */
  models: MyModelsOut | 'error' | null;
}

/**
 * Collapsible list of every guardrail in the caller's teams and the model ids it allows,
 * each with a copyable Claude Code `/model` command. Mirrors GET /api/me/models.
 */
export function ModelCatalog({ models }: ModelCatalogProps) {
  if (models !== null && models !== 'error' && models.teams.length === 0) return null;

  return (
    <details className="model-catalog">
      <summary>
        <h2 id="models-heading">Allowed models</h2>
        <span className="muted small">which models each guardrail permits, for Claude Code /model</span>
      </summary>
      <p className="muted">
        Your key's guardrail is shown in the Guardrail column of the key table. The same data is
        available from <span className="mono">GET /api/me/models</span>.
      </p>

      {models === null ? (
        <p className="muted" role="status">
          Loading guardrails...
        </p>
      ) : models === 'error' ? (
        <p className="muted">Could not load allowed models.</p>
      ) : (
        models.teams.map((team) => (
          <div key={team.team_id} className="model-team">
            <h3>{team.team_name}</h3>
            {!team.available ? (
              <p className="muted">Could not load this team's guardrails.</p>
            ) : team.guardrails.length === 0 ? (
              <p className="muted">No guardrails found for this workspace.</p>
            ) : (
              team.guardrails.map((g) => <GuardrailModels key={g.id} guardrail={g} />)
            )}
          </div>
        ))
      )}
    </details>
  );
}

function GuardrailModels({ guardrail }: { guardrail: GuardrailModelsOut }) {
  const label = guardrail.name ?? guardrail.id;

  return (
    <div className="model-guardrail" role="group" aria-label={`Guardrail ${label}`}>
      <h4>{label}</h4>
      {guardrail.description ? <p className="muted small">{guardrail.description}</p> : null}
      {guardrail.any_model ? (
        <p className="small">No model allowlist: any model OpenRouter offers is permitted.</p>
      ) : (
        <ul className="model-list">
          {guardrail.allowed_models.map((m) => (
            <li key={m.id}>
              <code className="mono">{m.command}</code>
              <CopyButton text={m.command} />
            </li>
          ))}
        </ul>
      )}
      {guardrail.ignored_models.length > 0 ? (
        <p className="small">
          Blocked: <span className="mono">{guardrail.ignored_models.join(', ')}</span>
        </p>
      ) : null}
    </div>
  );
}
